import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    hierarchyAdvisorApi,
    hierarchyKeys,
    httpStatusOf,
    runningReviewIdOf,
    type ApplyRequest,
    type ApplyResult,
    type Review,
    type Severity,
} from './api'

/** How often a running review is asked about: every 3 s (Andrew, 29 Sep 2026). */
export const REVIEW_POLL_MS = 3000

export function useHierarchyFindings(root: string | null | undefined, scenario: string | number, enabled = true) {
    return useQuery({
        queryKey: hierarchyKeys.findings(root ?? '', scenario),
        queryFn: () => hierarchyAdvisorApi.findings(root as string, scenario),
        enabled: enabled && !!root,
    })
}

export function useHierarchyRules(enabled = true) {
    return useQuery({ queryKey: hierarchyKeys.rules, queryFn: hierarchyAdvisorApi.rules, enabled })
}

export function useHierarchyExclusions(root: string | null | undefined, enabled = true) {
    return useQuery({
        queryKey: hierarchyKeys.exclusions(root ?? ''),
        queryFn: () => hierarchyAdvisorApi.exclusions(root as string),
        enabled: enabled && !!root,
    })
}

/**
 * Findings, and the exclusions list that explains them, are stale after any switch, exclusion or
 * decision. Every root's, not just this one's: an exclusion or a rule switched off hides that
 * finding under any root (contract, "Rules").
 */
function useInvalidateFindings() {
    const client = useQueryClient()
    return () =>
        Promise.all([
            client.invalidateQueries({ queryKey: hierarchyKeys.findingsAll }),
            client.invalidateQueries({ queryKey: hierarchyKeys.exclusionsAll }),
        ])
}

export function useSetRule() {
    const client = useQueryClient()
    const invalidate = useInvalidateFindings()
    return useMutation({
        mutationFn: (v: { ruleId: string; enabled: boolean; severity: Severity | null }) =>
            hierarchyAdvisorApi.putRule(v.ruleId, { enabled: v.enabled, severity: v.severity }),
        onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: hierarchyKeys.rules }), invalidate()]),
    })
}

export function useExclude() {
    const invalidate = useInvalidateFindings()
    return useMutation({
        mutationFn: hierarchyAdvisorApi.exclude,
        onSuccess: () => invalidate(),
    })
}

export function useRemoveExclusion() {
    const invalidate = useInvalidateFindings()
    return useMutation({
        mutationFn: hierarchyAdvisorApi.removeExclusion,
        onSuccess: () => invalidate(),
    })
}

export function useDecide() {
    const invalidate = useInvalidateFindings()
    return useMutation({
        mutationFn: (v: { adviceId: number; decision: 'accepted' | 'dismissed'; reason?: string }) =>
            hierarchyAdvisorApi.decide(v.adviceId, { decision: v.decision, reason: v.reason }),
        onSuccess: () => invalidate(),
    })
}

/**
 * Accept and do the work in one step: link an existing object, or create one and link it
 * (POST /advice/:id/apply). The findings are stale at once - the suggestion is no longer open, and
 * what was linked may change other findings too.
 *
 * `onApplied` is told first, and from the hook rather than the call: the card that asked leaves
 * the list when the findings refresh, and a callback given to mutate() is dropped with it.
 */
export function useApplyAdvice(onApplied?: (result: ApplyResult) => void) {
    const invalidate = useInvalidateFindings()
    return useMutation({
        mutationFn: (v: { adviceId: number; request: ApplyRequest }) => hierarchyAdvisorApi.apply(v.adviceId, v.request),
        onSuccess: (result) => {
            onApplied?.(result)
            return invalidate()
        },
    })
}

/**
 * The newest review of a root and scenario, from GET /reviews/latest: the one source of "is the
 * advisor running here", shared by everything in the panel whichever view is showing. The run
 * lives on the server, so this is only ever a reading of it.
 *
 * While the review it holds is running it is asked about every `pollMs` - by its id, not through
 * /latest: two POSTs at once leave the loser's stood-down row as the newest one, failed, while the
 * winner still runs. Polling stops on its own at done or failed, and when the panel unmounts (React
 * Query drops the interval with the last observer). When a run finishes, the findings are fetched
 * again, once, since that is where its suggestions arrive.
 */
export function useLatestReview(
    root: string | null | undefined,
    scenario: string | number,
    { enabled = true, pollMs = REVIEW_POLL_MS }: { enabled?: boolean; pollMs?: number } = {},
) {
    const client = useQueryClient()
    const queryKey = hierarchyKeys.latestReview(root ?? '', scenario)
    return useQuery({
        queryKey,
        queryFn: async (): Promise<Review | null> => {
            const held = client.getQueryData<Review | null>(queryKey)
            if (held?.status !== 'running') return hierarchyAdvisorApi.latestReview(root as string, scenario)
            const review = await hierarchyAdvisorApi.review(held.id)
            if (review.status !== 'running') {
                void client.invalidateQueries({ queryKey: hierarchyKeys.findings(root as string, scenario) })
            }
            return review
        },
        enabled: enabled && !!root,
        refetchInterval: (q) => (q.state.data?.status === 'running' ? pollMs : false),
    })
}

/**
 * POST /reviews. The review it starts becomes the latest review at once, so the panel shows it
 * running and polls it without waiting for a read. One run per root and scenario: a 409 names the
 * run already going, which is not an error but the review to watch.
 */
export function useStartReview() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: async (v: { root: string; scenario: string | number }) => {
            try {
                return await hierarchyAdvisorApi.startReview(v.root, v.scenario)
            } catch (error) {
                const id = httpStatusOf(error) === 409 ? runningReviewIdOf(error) : null
                if (id == null) throw error
                return { id, status: 'running' as const }
            }
        },
        // On the hook, not the call: it still lands if the view that asked has gone.
        onSuccess: async (started, v) => {
            const queryKey = hierarchyKeys.latestReview(v.root, v.scenario)
            // A read of /latest still in flight predates this run; it must not overwrite it.
            await client.cancelQueries({ queryKey })
            client.setQueryData<Review | null>(queryKey, {
                id: started.id,
                root: v.root,
                scenario: Number(v.scenario),
                status: 'running',
            })
        },
    })
}
